import styled from "styled-components";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { Paragraph } from "../../components/Paragraph";
import {
  formatShortDate,
  MatchContribution,
  MonitoringMatch,
} from "../../utils/monitoringScreening";

/**
 * Which run's evidence a match card is showing.
 *
 * A visible strip rather than a dropdown: the results are the subject of this
 * page, and hiding them behind a control would contradict that. One pill per run
 * that returned this subject, newest first.
 *
 * The strip drives the body below it and nothing above it. The status pill, the
 * decision buttons, the provenance line and the run history all belong to the
 * match, not to any one run, and stay put as the selection changes.
 */

const Pill = styled.button<{ isSelected: boolean }>`
  ${({ theme: { colors, fontFamily, fontSizes, fontWeights, radii, space }, isSelected }) => `
    display: inline-flex;
    align-items: center;
    gap: ${space[0]};
    font-family: ${fontFamily};
    font-size: ${fontSizes[0]};
    font-weight: ${isSelected ? fontWeights.semibold : fontWeights.regular};
    line-height: 1;
    cursor: pointer;
    padding: ${space[1]} ${space[2]};
    border-radius: ${radii[0]};
    border: 1px solid ${isSelected ? colors.blue.primary : colors.gray[20]};
    background: ${isSelected ? colors.blue[50] : colors.white};
    color: ${isSelected ? colors.blue[100] : colors.gray[60]};
    white-space: nowrap;

    &:hover {
      border-color: ${isSelected ? colors.blue.primary : colors.gray[40]};
    }

    &:focus-visible {
      outline: 2px solid ${colors.blue.primary};
      outline-offset: 2px;
    }
  `}
`;

const NewFlag = styled(Box)`
  ${({ theme: { colors, fontSizes, fontWeights, radii, space } }) => `
    display: inline-flex;
    font-size: ${fontSizes[0]};
    font-weight: ${fontWeights.semibold};
    letter-spacing: 0.04em;
    color: ${colors.orange[50]};
    background: ${colors.orange.primary};
    border-radius: ${radii[0]};
    padding: 0 ${space[0]};
  `}
`;

interface Props {
  match: MonitoringMatch;
  selected?: MatchContribution;
  onSelect: (activityId: number) => void;
}

export const EvidenceStrip = ({ match, selected, onSelect }: Props) => {
  // one run is the normal case on a freshly created record, and a strip of one
  // pill is chrome that says nothing
  if (match.contributions.length < 2) return null;

  const newest = match.contributions[0];
  const isShowingNewest = selected?.activityId === newest?.activityId;

  return (
    <Box mb={3}>
      <Flex alignItems="center" gap="8" flexWrap="wrap">
        <Paragraph mt={0} mb={0} fontSize={0} color="gray.50">
          Evidence
        </Paragraph>
        {match.contributions.map((contribution) => (
          <Pill
            key={contribution.activityId}
            type="button"
            isSelected={selected?.activityId === contribution.activityId}
            aria-pressed={selected?.activityId === contribution.activityId}
            onClick={() => onSelect(contribution.activityId)}
          >
            {formatShortDate(contribution.activityDate) || "Unknown date"}
            {contribution.isNew && <NewFlag>NEW</NewFlag>}
          </Pill>
        ))}
      </Flex>

      {/* a stale view must never be mistakeable for the current one */}
      {!isShowingNewest && selected && (
        <Paragraph mt={2} mb={0} fontSize={0} color="orange.50">
          Showing evidence from the run of{" "}
          {formatShortDate(selected.activityDate)}. This is not the most recent.
        </Paragraph>
      )}
    </Box>
  );
};
